import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search, ClipboardList, Ban } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

interface AuditMultiSelectProps {
  value: Id<"audits">[];
  onChange: (ids: Id<"audits">[]) => void;
  explicitNoAudit: boolean;
  onNoAuditChange: (value: boolean) => void;
}

interface AuditOption {
  id: Id<"audits">;
  displayName: string;
  code: string;
}

const ACTIVE_STATUSES = [
  "pending",
  "in_progress",
  "submitted",
  "approved",
  "rejected",
];

/**
 * Multi-select dropdown for choosing which audits to scope document uploads to.
 * Shown in documents-list mode (not audit-view mode).
 * 0 selected = workspace-wide (all active audits).
 */
export function AuditMultiSelect({
  value,
  onChange,
  explicitNoAudit,
  onNoAuditChange,
}: AuditMultiSelectProps) {
  const { t } = useTranslation("documents");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch active audits using the existing listAudits query with status filter.
  // Backend `api.audits.getActiveByWorkspace` does not exist yet, so we use
  // `listAudits` with a status filter as the equivalent.
  const { data: rawAudits } = useWorkspaceQuery(api.audit_queries.listAudits, {
    status: ACTIVE_STATUSES,
  });

  const audits: AuditOption[] = (rawAudits ?? []).map((a) => ({
    id: a.id,
    displayName: a.displayName,
    code: a.code,
  }));

  const filteredAudits = query
    ? audits.filter(
        (a) =>
          a.displayName.toLowerCase().includes(query.toLowerCase()) ||
          a.code.toLowerCase().includes(query.toLowerCase()),
      )
    : audits;

  const handleToggle = (auditId: Id<"audits">) => {
    // Selecting an audit clears "No audit" flag
    if (explicitNoAudit) {
      onNoAuditChange(false);
    }
    if (value.includes(auditId)) {
      onChange(value.filter((id) => id !== auditId));
    } else {
      onChange([...value, auditId]);
    }
    setQuery("");
    inputRef.current?.focus();
  };

  const handleSelectNoAudit = () => {
    // "No audit" is mutually exclusive with audit selections
    onNoAuditChange(!explicitNoAudit);
    if (!explicitNoAudit) {
      onChange([]);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      // Small delay to let the popover mount before focusing
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const selectedAudits = audits.filter((a) => value.includes(a.id));

  return (
    <div className="mb-4">
      <label
        htmlFor="audit-context-select"
        className="text-xs font-medium text-secondary mb-1.5 block"
      >
        {t("upload.auditSelect.label")}
      </label>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            id="audit-context-select"
            type="button"
            className="w-full min-h-[42px] rounded-lg border border-border bg-card px-3 py-2 flex flex-wrap gap-1.5 items-center text-left hover:border-border-hover transition-colors"
          >
            {explicitNoAudit ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-muted/10 px-2 py-0.5 text-xs font-medium text-muted">
                <Ban size={12} className="shrink-0" />
                {t("upload.auditSelect.noAuditBadge")}
              </span>
            ) : selectedAudits.length === 0 ? (
              <span className="text-sm text-muted">
                {t("upload.auditSelect.placeholder")}
              </span>
            ) : (
              selectedAudits.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1 rounded-md bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
                >
                  <ClipboardList size={12} className="shrink-0" />
                  {a.displayName}
                </span>
              ))
            )}
            <ChevronDown size={14} className="ml-auto shrink-0 text-muted" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="bg-card rounded-[12px] border border-border shadow-lg z-50 w-[320px]"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Search input */}
            <div className="p-2 border-b border-border flex items-center gap-2">
              <Search size={14} className="text-muted shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("upload.auditSelect.searchPlaceholder")}
                className="w-full bg-transparent text-sm text-primary placeholder:text-muted outline-none"
              />
            </div>

            {/* No audit option */}
            <div className="p-1 border-b border-border">
              <button
                type="button"
                onClick={handleSelectNoAudit}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-[8px] transition-colors cursor-pointer text-left ${
                  explicitNoAudit
                    ? "bg-muted/10 text-primary"
                    : "text-secondary hover:text-primary hover:bg-card-hover"
                }`}
              >
                <Ban size={14} className="shrink-0 text-muted" />
                <span className="flex-1">
                  {t("upload.auditSelect.noAuditOption")}
                </span>
                {explicitNoAudit && (
                  <Check size={14} className="shrink-0 text-accent" />
                )}
              </button>
            </div>

            {/* Options list */}
            {filteredAudits.length > 0 && (
              <div className="max-h-[240px] overflow-y-auto overscroll-contain p-1">
                {filteredAudits.map((audit) => {
                  const isSelected = value.includes(audit.id);
                  return (
                    <button
                      key={audit.id}
                      type="button"
                      onClick={() => handleToggle(audit.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-[8px] transition-colors cursor-pointer text-left ${
                        isSelected
                          ? "bg-accent/10 text-primary"
                          : "text-secondary hover:text-primary hover:bg-card-hover"
                      }`}
                    >
                      <ClipboardList
                        size={14}
                        className="shrink-0 text-muted"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="truncate block">
                          {audit.displayName}
                        </span>
                        {audit.code && (
                          <span className="text-xs text-muted">
                            {audit.code}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <Check size={14} className="shrink-0 text-accent" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {filteredAudits.length === 0 && (
              <div className="p-4 text-center text-sm text-muted">
                {query
                  ? t("upload.auditSelect.noResults")
                  : t("upload.auditSelect.noAudits")}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
